import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import Modal from "react-modal";
import { CircularProgress } from "@mui/material";
import { LuStore } from "react-icons/lu";
import { FiList } from "react-icons/fi";

import { businessWinnerDetailsActions } from "../../store/businessWinnerDetailsSlice";
import { useGetBusinessWinnerByIdQuery } from "../../store/services/businessWinnersServices";

const customStyles = {
  content: {
    position: "static",
    borderRadius: "10px",
    padding: 0,
    border: "none",
  },
  overlay: {
    background: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 50,
  },
};

if (typeof document !== "undefined" && document.getElementById("root")) {
  Modal.setAppElement(document.getElementById("root"));
}

const formatMoney = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return `€${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB");
  } catch {
    return "—";
  }
};

const FISCAL_PARITY_LABEL = {
  all: "Të gjithë",
  odd: "Tek",
  even: "Çift",
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-center gap-3">
    <div className="text-xs uppercase text-gray-500">{label}</div>
    <div className="text-xs font-medium text-gray-800 text-right truncate max-w-[60%]">
      {value || "—"}
    </div>
  </div>
);

export default function BusinessWinnerDetails() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const winner = useSelector((state) => state.business_winner_details.data);
  const isOpen = useSelector((state) => state.business_winner_details.isOpen);

  const winnerId = winner?.business_winner_id;
  const { data: detailsResponse, isLoading } = useGetBusinessWinnerByIdQuery(
    winnerId || "",
    { skip: !winnerId }
  );

  const data = detailsResponse?.data || winner || {};

  const closeModal = () => dispatch(businessWinnerDetailsActions.closeModal());

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      style={customStyles}
      contentLabel="Business Winner Details"
    >
      <div className="bg-white rounded-[10px] w-[380px] max-w-[90vw] text-sm">
        {isLoading ? (
          <div className="flex justify-center items-center h-[420px]">
            <CircularProgress />
          </div>
        ) : (
          <div className="flex flex-col gap-4 p-5">
            <div className="flex flex-col gap-2 items-center">
              <div className="w-14 h-14 rounded-full flex items-center justify-center border border-gray-200 bg-gray-50">
                <LuStore size={26} className="text-gray-500" />
              </div>
              <div className="text-lg font-semibold text-gray-900 text-center truncate max-w-full">
                {data.business_name || "—"}
              </div>
              {data.business_nui && (
                <div className="text-xs text-gray-500 tabular-nums">
                  NUI {data.business_nui}
                </div>
              )}
            </div>

            <hr className="w-full" />

            <div className="flex flex-col gap-2 w-full">
              <Row label="ID" value={`#${data.business_winner_id ?? ""}`} />
              <Row label="Fiscal #" value={data.business_fiscal_number} />
              <Row label="Adresa" value={data.business_address} />
              <Row label="Qyteti" value={data.business_city} />
              <Row label="Komuna" value={data.business_municipality_no} />
              <Row label="Telefoni" value={data.business_phone} />
              <Row label="Email" value={data.business_email} />
            </div>

            <hr className="w-full" />

            <div className="flex flex-col gap-2 w-full">
              <Row label="Loja" value={data.game_title} />
              <Row label="Tipi i lojës" value={data.game_type} />
              {data.fiscal_parity && (
                <Row
                  label="Përzgjedhje sipas"
                  value={FISCAL_PARITY_LABEL[data.fiscal_parity] || data.fiscal_parity}
                />
              )}
              <Row label="Data e shpërblimit" value={formatDate(data.created_at)} />
            </div>

            <hr className="w-full" />

            <div className="flex flex-col gap-2 w-full">
              <Row label="Çmimi" value={formatMoney(data.prize_amount)} />
              {data.prize_description && (
                <Row label="Përshkrimi" value={data.prize_description} />
              )}
              <Row label="Shuma e kuponit" value={formatMoney(data.receipt_amount)} />
              {data.receipt_vat !== undefined && (
                <Row label="TVSH" value={formatMoney(data.receipt_vat)} />
              )}
              <Row label="Përdoruesi" value={data.user_name} />
              {data.user_email && <Row label="Email përdoruesi" value={data.user_email} />}
              {data.user_phone && <Row label="Telefoni përdoruesi" value={data.user_phone} />}
            </div>

            <hr className="w-full" />

            <button
              type="button"
              onClick={() => {
                closeModal();
                if (data.receipt_id) {
                  navigate(`/approved_receipts?receipt_id=${data.receipt_id}`);
                }
              }}
              disabled={!data.receipt_id}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--secondary-color)] hover:bg-[var(--secondary-color)]/90 active:scale-[0.98] py-2.5 text-xs font-bold uppercase tracking-wide text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiList size={16} />
              Shiko kuponin
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
